import { Callout } from "../components";

export function Financials() {
  return (
    <>
      <h1>Financials</h1>

      <p>
        Four founders who bootstrapped a EUR 30M+ company know how to make
        money last. TAIGA applies that discipline to venture capital —
        non-dilutive funding secured before the pre-seed close, and revenue
        from Month 4.
      </p>

      <hr />

      <h2>The Headline Numbers</h2>
      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Revenue Start</td>
            <td>Month 4 (April 2026) at EUR 5,000</td>
          </tr>
          <tr>
            <td>Revenue Month 24</td>
            <td>EUR 468k/month (~EUR 5.6M ARR run rate)</td>
          </tr>
          <tr>
            <td>Monthly Growth Rate</td>
            <td>~20%</td>
          </tr>
          <tr>
            <td>Gross Margin</td>
            <td>80%</td>
          </tr>
          <tr>
            <td>EBITDA Positive</td>
            <td>Month 23 (November 2027)</td>
          </tr>
          <tr>
            <td>ARR Target (end 2027)</td>
            <td>EUR 8M</td>
          </tr>
          <tr>
            <td>Cash Position Month 24</td>
            <td>~EUR 3.37M</td>
          </tr>
          <tr>
            <td>Growth Trajectory</td>
            <td>10x year-over-year</td>
          </tr>
        </tbody>
      </table>

      <Callout variant="info" title="Model vs. Actual">
        The financial model below was built in February 2026, before the
        company incorporated or signed customers. Actual traction is running
        ahead of model — first customer contracts were signed within weeks
        of incorporation and EUR 10k+ MRR is closing before Month 4. The
        model remains useful as a conservative baseline but understates
        early momentum.
      </Callout>

      <hr />

      <h2>Revenue Trajectory</h2>
      <p>From EUR 5k to EUR 468k per month in 24 months.</p>

      <table>
        <thead>
          <tr>
            <th>Period</th>
            <th>Monthly Revenue</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Apr 2026 (M4)</td>
            <td>EUR 5,000</td>
          </tr>
          <tr>
            <td>Jun 2026 (M6)</td>
            <td>EUR 7,200</td>
          </tr>
          <tr>
            <td>Sep 2026 (M9)</td>
            <td>EUR 12,442</td>
          </tr>
          <tr>
            <td>Dec 2026 (M12)</td>
            <td>EUR 21,499 (~EUR 100k Year 1)</td>
          </tr>
          <tr>
            <td>Mar 2027 (M15)</td>
            <td>EUR 37,151</td>
          </tr>
          <tr>
            <td>Jun 2027 (M18)</td>
            <td>EUR 64,186</td>
          </tr>
          <tr>
            <td>Sep 2027 (M21)</td>
            <td>EUR 110,885</td>
          </tr>
          <tr>
            <td>Dec 2027 (M24)</td>
            <td>EUR 467,988 (~EUR 5.6M ARR run rate)</td>
          </tr>
        </tbody>
      </table>

      <p>
        The financial model shows ~EUR 5.6M ARR run rate by end of 2027. The
        stated EUR 8M ARR target assumes acceleration from Seed-funded
        growth — expanded sales team, marketing investment, and partner
        channel coming online.
      </p>

      <hr />

      <h2>How We Spend</h2>
      <p>
        Lean by design. The biggest line item is people — not office space,
        not marketing vanity metrics.
      </p>

      <p>
        <strong>COGS:</strong> 20% of revenue (LLM API costs, compute).
        Gross margin: 80%.
      </p>

      <table>
        <thead>
          <tr>
            <th>Category</th>
            <th>M3 (Mar 26)</th>
            <th>M12 (Dec 26)</th>
            <th>M24 (Dec 27)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Salaries</td>
            <td>EUR 20,000</td>
            <td>EUR 60,000</td>
            <td>EUR 200,000</td>
          </tr>
          <tr>
            <td>Side costs (25%)</td>
            <td>EUR 5,000</td>
            <td>EUR 15,000</td>
            <td>EUR 50,000</td>
          </tr>
          <tr>
            <td>Rent</td>
            <td>EUR 500</td>
            <td>EUR 500</td>
            <td>EUR 3,000</td>
          </tr>
          <tr>
            <td>Marketing</td>
            <td>EUR 0</td>
            <td>EUR 100,000</td>
            <td>EUR 100,000</td>
          </tr>
          <tr>
            <td>Other costs</td>
            <td>EUR 4,000</td>
            <td>EUR 12,000</td>
            <td>EUR 40,000</td>
          </tr>
        </tbody>
      </table>

      <h3>Monthly P&amp;L Summary</h3>
      <table>
        <thead>
          <tr>
            <th>Line Item</th>
            <th>M3</th>
            <th>M6</th>
            <th>M9</th>
            <th>M12</th>
            <th>M18</th>
            <th>M24</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Revenue</td>
            <td>EUR 0</td>
            <td>EUR 7,200</td>
            <td>EUR 12,442</td>
            <td>EUR 21,499</td>
            <td>EUR 64,186</td>
            <td>EUR 467,988</td>
          </tr>
          <tr>
            <td>COGS (20%)</td>
            <td>EUR 0</td>
            <td>-EUR 1,440</td>
            <td>-EUR 2,488</td>
            <td>-EUR 4,300</td>
            <td>-EUR 12,837</td>
            <td>-EUR 93,598</td>
          </tr>
          <tr>
            <td>Gross Profit</td>
            <td>EUR 0</td>
            <td>EUR 5,760</td>
            <td>EUR 9,954</td>
            <td>EUR 17,199</td>
            <td>EUR 51,349</td>
            <td>EUR 374,390</td>
          </tr>
          <tr>
            <td>Salaries + side costs</td>
            <td>-EUR 25,000</td>
            <td>-EUR 31,250</td>
            <td>-EUR 62,500</td>
            <td>-EUR 75,000</td>
            <td>-EUR 187,500</td>
            <td>-EUR 250,000</td>
          </tr>
          <tr>
            <td>Rent</td>
            <td>-EUR 500</td>
            <td>-EUR 500</td>
            <td>-EUR 500</td>
            <td>-EUR 500</td>
            <td>-EUR 7,500</td>
            <td>-EUR 3,000</td>
          </tr>
          <tr>
            <td>Marketing</td>
            <td>EUR 0</td>
            <td>EUR 0</td>
            <td>-EUR 100,000</td>
            <td>-EUR 100,000</td>
            <td>-EUR 100,000</td>
            <td>-EUR 100,000</td>
          </tr>
          <tr>
            <td>Other costs</td>
            <td>-EUR 4,000</td>
            <td>-EUR 6,000</td>
            <td>-EUR 10,000</td>
            <td>-EUR 12,000</td>
            <td>-EUR 24,000</td>
            <td>-EUR 40,000</td>
          </tr>
          <tr>
            <td>
              <strong>EBITDA</strong>
            </td>
            <td>
              <strong>-EUR 29,500</strong>
            </td>
            <td>
              <strong>-EUR 31,990</strong>
            </td>
            <td>
              <strong>-EUR 163,046</strong>
            </td>
            <td>
              <strong>-EUR 170,301</strong>
            </td>
            <td>
              <strong>-EUR 277,651</strong>
            </td>
            <td>
              <strong>-EUR 28,610</strong>
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        EBITDA turns positive in Month 23 (Nov 2027) at EUR 11,490, reaching
        EUR 74,988 in Month 24. Major marketing investment aligned with
        seed round close, followed by EUR 100k/month sustained spend.
      </p>

      <hr />

      <h2>Funding</h2>
      <p>Three rounds. Each one tied to clear milestones.</p>

      <table>
        <thead>
          <tr>
            <th>Round</th>
            <th>Timing</th>
            <th>Amount</th>
            <th>Valuation</th>
            <th>Dilution</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Angels &amp; Advisors</td>
            <td>Mar 2026</td>
            <td>EUR 150,000</td>
            <td>EUR 5,000,000</td>
            <td>~3%</td>
            <td>CLOSED</td>
          </tr>
          <tr>
            <td>VC Pre-seed</td>
            <td>May 2026</td>
            <td>EUR 2,000,000</td>
            <td>EUR 20,000,000</td>
            <td>10%</td>
            <td>RAISING</td>
          </tr>
          <tr>
            <td>VC Seed</td>
            <td>Winter 2026-27</td>
            <td>EUR 10,000,000</td>
            <td>EUR 100,000,000</td>
            <td>10%</td>
            <td>Planned</td>
          </tr>
        </tbody>
      </table>

      <h3>Non-Dilutive Funding</h3>
      <table>
        <thead>
          <tr>
            <th>Instrument</th>
            <th>Amount</th>
            <th>Type</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Finnvera Start Guarantee</td>
            <td>EUR 200,000</td>
            <td>Loan</td>
            <td>Secured</td>
          </tr>
          <tr>
            <td>Business Finland Sprint</td>
            <td>EUR 100,000</td>
            <td>Grant</td>
            <td>Near-certain</td>
          </tr>
          <tr>
            <td>Business Finland R&amp;D</td>
            <td>EUR 250,000</td>
            <td>Grant/Loan</td>
            <td>Applied, medium confidence</td>
          </tr>
          <tr>
            <td>
              <strong>Total non-dilutive</strong>
            </td>
            <td>
              <strong>EUR 300k secured + EUR 250k pipeline</strong>
            </td>
            <td></td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <p>
        Finnvera Start Guarantee is a subsidized loan (repayable, but
        favorable terms). Business Finland Sprint is a non-repayable grant.
        Business Finland R&amp;D can be structured as grant, loan, or
        combination depending on the instrument. Net effect: EUR 100k is
        fully non-dilutive (grant), EUR 200k is subsidized debt, EUR 250k
        pipeline is TBD. All reduce equity dilution compared to raising the
        equivalent as venture capital.
      </p>

      <hr />

      <h2>Use of Funds</h2>
      <p>
        <strong>Angels &amp; Advisors (EUR 150k — CLOSED):</strong>{" "}
        Establish operations, build prototype, get first customers.
      </p>

      <p>
        <strong>VC Pre-seed (EUR 2M — RAISING):</strong>
      </p>

      <table>
        <thead>
          <tr>
            <th>Allocation</th>
            <th>%</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Product &amp; engineering</td>
            <td>~50%</td>
          </tr>
          <tr>
            <td>Customer delivery &amp; pilots</td>
            <td>~20%</td>
          </tr>
          <tr>
            <td>Team expansion</td>
            <td>~15%</td>
          </tr>
          <tr>
            <td>Marketing</td>
            <td>~10%</td>
          </tr>
          <tr>
            <td>Operations</td>
            <td>~5%</td>
          </tr>
        </tbody>
      </table>

      <p>
        Milestones: production-ready product, 10+ pilot customers, EUR 60k
        MRR.
      </p>

      <p>
        <strong>VC Seed (EUR 10M — planned, Winter 2026-27):</strong> Launch
        and market entry, scale sales and marketing. Milestones: market
        launch, international expansion, EUR 8M ARR run rate.
      </p>

      <hr />

      <h2>Capital Efficiency</h2>
      <p>
        These are not talking points. They are operational reality —
        principles forged over a decade of building without a safety net.
      </p>

      <p>
        <strong>Bootstrap DNA.</strong> Cloud2 scaled to EUR 30M+ without
        external funding. That instinct does not disappear when you take
        venture money. Every euro gets interrogated before it gets spent.
      </p>
      <p>
        <strong>Compute over headcount.</strong> TAIGA is AI-native from the
        ground up. The architecture is designed so that compute scales
        where headcount traditionally would. Fewer people, more leverage.
      </p>
      <p>
        <strong>Revenue from Month 4.</strong> We are not burning cash
        waiting for product-market fit. Paying customers from the start
        means the model validates itself in real time.
      </p>
      <p>
        <strong>Non-dilutive capital.</strong> EUR 300k secured (Finnvera +
        BF Sprint), EUR 250k R&amp;D funding in pipeline. Finnish public
        instruments reduce equity dilution. Free money is better than cheap
        money, and cheap money is better than equity.
      </p>

      <hr />

      <h2>Cash Position</h2>
      <table>
        <thead>
          <tr>
            <th>Timing</th>
            <th>Cash Position</th>
            <th>Key Event</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Mar 2026</td>
            <td>~EUR 350k</td>
            <td>Angels (EUR 150k) + Finnvera (EUR 200k)</td>
          </tr>
          <tr>
            <td>Apr 2026</td>
            <td>~EUR 450k</td>
            <td>BF Sprint grant (EUR 100k)</td>
          </tr>
          <tr>
            <td>May 2026</td>
            <td>~EUR 2.4M</td>
            <td>VC Pre-seed (EUR 2M)</td>
          </tr>
          <tr>
            <td>Winter 2026-27</td>
            <td>~EUR 12.4M</td>
            <td>VC Seed (EUR 10M) — peak</td>
          </tr>
          <tr>
            <td>Month 12</td>
            <td>~EUR 11M+</td>
            <td>Growth spending</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>Customer Lifetime Value</h2>
      <p>
        Governance creates switching costs. Once a customer's policies,
        compliance mappings, and audit trails live in TAIGA, leaving is
        expensive.
      </p>

      <table>
        <thead>
          <tr>
            <th>Metric</th>
            <th>Value</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Initial MVP</td>
            <td>EUR 15-25k</td>
          </tr>
          <tr>
            <td>Feature additions/year</td>
            <td>4-8 at EUR 2-8k</td>
          </tr>
          <tr>
            <td>Annual recurring</td>
            <td>EUR 25-90k</td>
          </tr>
          <tr>
            <td>Retention</td>
            <td>&gt;90% (governance has high switching costs)</td>
          </tr>
          <tr>
            <td>Estimated 3-year LTV</td>
            <td>EUR 75-270k</td>
          </tr>
          <tr>
            <td>Target ARR per customer</td>
            <td>EUR 80-200k/year</td>
          </tr>
          <tr>
            <td>NRR target</td>
            <td>&gt;130% (enterprise land-and-expand)</td>
          </tr>
        </tbody>
      </table>

      <h3>Operating Leverage</h3>
      <p>
        The model achieves profitability at ~EUR 500k/month revenue with ~20
        people — reachable by Month 24.
      </p>

      <hr />

      <h2>Team Growth</h2>
      <table>
        <thead>
          <tr>
            <th>Period</th>
            <th>Monthly Salary</th>
            <th>Implied Team Size</th>
            <th>Focus</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>M1-2</td>
            <td>EUR 0</td>
            <td>4 founders (unpaid)</td>
            <td>MVP development</td>
          </tr>
          <tr>
            <td>M3</td>
            <td>EUR 20,000</td>
            <td>4 founders</td>
            <td>MVP launch</td>
          </tr>
          <tr>
            <td>M6</td>
            <td>EUR 25,000</td>
            <td>4 founders + ramp</td>
            <td>First pilots</td>
          </tr>
          <tr>
            <td>M12</td>
            <td>EUR 60,000</td>
            <td>~8-10 people</td>
            <td>Customer delivery</td>
          </tr>
          <tr>
            <td>M18</td>
            <td>EUR 150,000</td>
            <td>~15 people</td>
            <td>Platform development</td>
          </tr>
          <tr>
            <td>M24</td>
            <td>EUR 200,000</td>
            <td>~20 people</td>
            <td>Scale operations</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <h2>Assumptions and Scenarios</h2>
      <p>
        We model three scenarios. The base case is aggressive but
        achievable.
      </p>

      <h3>Key Assumptions</h3>
      <p>
        <strong>Revenue:</strong>
      </p>
      <ul>
        <li>
          First revenue Month 4 at EUR 5k — <strong>High</strong> confidence
        </li>
        <li>
          ~20% MoM growth — <strong>Medium</strong> confidence
        </li>
        <li>
          COGS at 20% — <strong>High</strong> confidence (likely to improve)
        </li>
        <li>
          &gt;90% customer retention — <strong>High</strong> confidence
        </li>
      </ul>

      <p>
        <strong>Funding:</strong>
      </p>
      <ul>
        <li>
          Pre-seed Month 3 — <strong>High</strong> confidence
        </li>
        <li>
          Seed Winter 2026-27 — <strong>Medium</strong> confidence
        </li>
        <li>
          BF Sprint EUR 100k — <strong>High</strong> confidence
          (near-certain)
        </li>
        <li>
          BF R&amp;D EUR 250k — <strong>Medium</strong> confidence
        </li>
      </ul>

      <p>
        <strong>Costs:</strong>
      </p>
      <ul>
        <li>
          Founder salaries included from M3 — <strong>High</strong>{" "}
          confidence
        </li>
        <li>
          25% Finnish employer side costs — <strong>High</strong> confidence
        </li>
        <li>
          Marketing EUR 500k spike Month 7 — <strong>Medium</strong>{" "}
          confidence (can reduce)
        </li>
      </ul>

      <p>
        <strong>Market:</strong>
      </p>
      <ul>
        <li>
          EU AI Act 2026 enforcement on schedule — <strong>High</strong>{" "}
          confidence
        </li>
        <li>
          Enterprise AI adoption accelerating — <strong>High</strong>{" "}
          confidence
        </li>
        <li>
          Governance becomes buying criterion — <strong>Medium-High</strong>{" "}
          confidence
        </li>
        <li>
          No dominant competitor in 12 months — <strong>Medium</strong>{" "}
          confidence
        </li>
      </ul>

      <h3>Sensitivity Analysis</h3>
      <table>
        <thead>
          <tr>
            <th>Scenario</th>
            <th>Growth Rate</th>
            <th>M12 Revenue</th>
            <th>M24 Revenue</th>
            <th>Break-even</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Bear</td>
            <td>15%/month</td>
            <td>~EUR 14k</td>
            <td>~EUR 200k</td>
            <td>Month 28+</td>
          </tr>
          <tr>
            <td>Base</td>
            <td>20%/month</td>
            <td>~EUR 21k</td>
            <td>~EUR 468k</td>
            <td>Month 23</td>
          </tr>
          <tr>
            <td>Bull</td>
            <td>25%/month</td>
            <td>~EUR 30k</td>
            <td>~EUR 1M+</td>
            <td>Month 19</td>
          </tr>
        </tbody>
      </table>
    </>
  );
}
